package com.ijoin.ui.profile

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.ijoin.data.model.User
import com.ijoin.data.repository.AuthRepository
import com.ijoin.i18n.AppLanguage
import com.ijoin.i18n.SupportedLanguages
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class ProfileViewModel(
    private val authRepository: AuthRepository = AuthRepository()
) : ViewModel() {

    private val _uiState = MutableStateFlow(ProfileUiState())
    val uiState: StateFlow<ProfileUiState> = _uiState.asStateFlow()

    init {
        refreshProfile()
    }

    fun refreshProfile() {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true, errorMessage = null) }
            authRepository.getCurrentUser()
                .onSuccess { user ->
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            user = user,
                            editableUser = EditableUserFields.from(user),
                            errorMessage = null
                        )
                    }
                }
                .onFailure { error ->
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            errorMessage = error.message ?: "Failed to load profile"
                        )
                    }
                }
        }
    }

    fun startEditing() {
        _uiState.update {
            if (it.user == null) it else it.copy(isEditing = true)
        }
    }

    fun cancelEditing() {
        _uiState.update {
            it.copy(
                isEditing = false,
                editableUser = it.user?.let(EditableUserFields::from) ?: EditableUserFields()
            )
        }
    }

    fun updateEditableUser(transform: (EditableUserFields) -> EditableUserFields) {
        _uiState.update {
            it.copy(editableUser = transform(it.editableUser))
        }
    }

    fun saveProfileChanges() {
        if (_uiState.value.user == null) return
        val fields = _uiState.value.editableUser

        viewModelScope.launch {
            _uiState.update { it.copy(isSaving = true, errorMessage = null) }

            authRepository.updateProfile(
                username = fields.username,
                firstName = fields.firstName.ifBlank { null },
                lastName = fields.lastName.ifBlank { null },
                phone = fields.phone.ifBlank { null },
                avatarDataUrl = fields.avatarDataUrl,
                language = fields.language.code
            )
                .onSuccess { updatedUser ->
                    _uiState.update {
                        it.copy(
                            user = updatedUser,
                            editableUser = EditableUserFields.from(updatedUser),
                            isEditing = false,
                            isSaving = false,
                            message = ProfileMessage.Saved
                        )
                    }
                }
                .onFailure { error ->
                    _uiState.update {
                        it.copy(
                            isSaving = false,
                            errorMessage = error.message ?: "Failed to save profile"
                        )
                    }
                }
        }
    }

    fun setAvatarDataUrl(dataUrl: String?) {
        _uiState.update { state ->
            state.copy(
                editableUser = state.editableUser.copy(avatarDataUrl = dataUrl)
            )
        }
    }

    fun clearMessage() {
        _uiState.update { it.copy(message = null) }
    }

    fun clearError() {
        _uiState.update { it.copy(errorMessage = null) }
    }

    fun deleteAccount() {
        val currentUser = _uiState.value.user ?: run {
            _uiState.update { it.copy(errorMessage = "Not authenticated") }
            return
        }
        viewModelScope.launch {
            _uiState.update { it.copy(isDeleting = true, errorMessage = null) }
            authRepository.deleteAccount()
                .onSuccess {
                    _uiState.update {
                        it.copy(
                            isDeleting = false,
                            message = ProfileMessage.Deleted,
                            user = null,
                        )
                    }
                }
                .onFailure { error ->
                    _uiState.update {
                        it.copy(
                            isDeleting = false,
                            errorMessage = error.message ?: "Failed to delete account"
                        )
                    }
                }
        }
    }
}

sealed interface ProfileMessage {
    data object Saved : ProfileMessage
    data object Deleted : ProfileMessage
}

data class EditableUserFields(
    val username: String = "",
    val firstName: String = "",
    val lastName: String = "",
    val phone: String = "",
    val avatarUrl: String? = null,
    val avatarDataUrl: String? = null,
    val language: AppLanguage = SupportedLanguages.default
) {
    companion object {
        fun from(user: User): EditableUserFields = EditableUserFields(
            username = user.username,
            firstName = user.firstName.orEmpty(),
            lastName = user.lastName.orEmpty(),
            phone = user.phone.orEmpty(),
            avatarUrl = user.avatarUrl,
            avatarDataUrl = null,
            language = SupportedLanguages.fromCode(user.language)
        )
    }
}

data class ProfileUiState(
    val isLoading: Boolean = true,
    val isSaving: Boolean = false,
    val user: User? = null,
    val isEditing: Boolean = false,
    val editableUser: EditableUserFields = EditableUserFields(),
    val errorMessage: String? = null,
    val message: ProfileMessage? = null,
    val isDeleting: Boolean = false
)
